import clsx from 'clsx';
import { isEmpty } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, CampTableCard, Card, ReactTable, Skeleton, ZendeskLink } from 'components';
import { formatDate, getValidDate, normalizeStr } from 'helpers';
import { useFetch, useSafeState, useUser } from 'hooks';

const orderFilters = ['all', 'onGoing', 'validated', 'canceled'];

const DashboardDocument = () => {
  const { t } = useTranslation();
  const { currentUser } = useUser();

  const { call, loading, data } = useFetch();

  const [currentFilter, setCurrentFilter] = useSafeState(orderFilters[0]);
  const [filteredOrders, setFilteredOrders] = useSafeState(null);

  useEffect(() => {
    getDocument();
  }, []);

  const getDocument = async () => {
    const { data } =
      (await call({
        service: 'castelis',
        path: `/order/camp/${currentUser.serialNumber}`,
        method: 'get'
      })) || {};

    setFilteredOrders(data?.body?.orders);
  };

  const orderColumns = useMemo(
    () => [
      {
        accessorKey: 'date',
        enableSorting: false,
        header: t('dashboard:table.header.date'),
        cell: (cell) => <span>{formatDate(getValidDate(cell?.getValue()))}</span>
      },
      {
        accessorKey: 'partner',
        enableSorting: false,
        header: t('dashboard:table.header.partner')
      },
      {
        accessorKey: 'fromDate',
        enableSorting: false,
        header: t('dashboard:table.header.fromDate')
      },
      {
        accessorKey: 'toDate',
        enableSorting: false,
        header: t('dashboard:table.header.toDate')
      },
      {
        accessorKey: 'status',
        header: t('dashboard:table.header.status'),
        enableSorting: false,
        cell: (cell) => {
          let badgeColor = 'yellow';

          switch (cell.getValue()) {
            case 'Annulée':
              badgeColor = 'red';
              break;
            default:
              break;
          }

          return <Badge color={badgeColor}>{cell.getValue()}</Badge>;
        }
      },
      {
        accessorKey: 'action',
        header: t('dashboard:table.header.action'),
        enableSorting: false,
        cell: (cell) => {
          if (cell?.row?.original?.url && cell?.getValue()) {
            return (
              <ZendeskLink to={cell?.row?.original?.url} className="btn small text-sm font-semibold">
                <span className="truncate-line one-line">{cell?.getValue()}</span>
              </ZendeskLink>
            );
          }
        }
      }
    ],
    [filteredOrders]
  );

  const compareStrings = (string, searchValue) => {
    return normalizeStr(string).includes(normalizeStr(searchValue));
  };

  const onFilterOrders = (filter) => {
    if (filter === currentFilter) return;

    const orders = data?.orders?.filter((order) => {
      if (filter === 'onGoing' && compareStrings(order.status, 'En Cours')) return order;
      if (filter === 'validated' && compareStrings(order.status, 'Validée')) return order;
      if (filter === 'canceled' && compareStrings(order.status, 'Annulée')) return order;
      if (filter === 'all') return order;
      return false;
    });

    setCurrentFilter(filter);
    setFilteredOrders(orders);
  };

  return (
    <>
      {loading && <Skeleton classNames="h-96 w-full mt-4" />}

      {!loading && filteredOrders && (
        <>
          <Card className="hidden lg:block">
            <div className="flex-center-between flex-wrap mb-6">
              <h2 className="h2 underlined-yellow">{t('dashboard:titles.documents')}</h2>

              <ZendeskLink to={process.env.REACT_APP_ZENDESK_URL_ORDER_CAMPING} className="btn">
                {t('dashboard:addRequest')}
              </ZendeskLink>
            </div>
            {filteredOrders.length > 0 ? (
              <ReactTable columns={orderColumns} data={filteredOrders} pagination={filteredOrders.length > 10} />
            ) : (
              <p>{t('dashboard:noDocuments')}</p>
            )}
          </Card>

          {/* Mobile display */}
          <div className="flex gap-2 my-4 flex-wrap lg:hidden">
            {!isEmpty(filteredOrders) &&
              orderFilters.map((filter, index) => (
                <button
                  type="button"
                  className={clsx('filter-btn', currentFilter === filter && 'active')}
                  key={`order-filter-${index}`}
                  onClick={() => onFilterOrders(filter)}
                >
                  {t(`buttons.${filter}`)}
                </button>
              ))}
          </div>

          <div className="grid gap-4 lg:hidden">
            {isEmpty(filteredOrders) && <p>{t('dashboard:noDocuments')}</p>}

            {!isEmpty(filteredOrders) &&
              filteredOrders.map((order, i) => {
                return <CampTableCard row={order} key={`row-${i}`} />;
              })}
          </div>
        </>
      )}
    </>
  );
};

export default DashboardDocument;
